import { postForecastRow } from "../services/forecastRowApi.js";
import { useState, useRef, useCallback, useEffect } from "react";
import { recordUiEvent } from "../lib/logRingBuffer";
import { emitTourEnd } from "../lib/telemetry/uiIntents.js";
import { persistMessage } from "../lib/messages";
import { activeSet, traceAllocated, entryAppended } from "../lib/forecast/reducer.js";
import { startCustomTriggerNudge } from "../lib/forecast/customTriggerNudge.js";
import { makeEntry } from "../lib/forecast/model.js";
import {
  runSayerTour,
  makeTourToken,
  SAYER_STEPS,
  TOUR_QUESTION,
  TOUR_INTRO,
  TOUR_QUESTION_FORMAT,
  TOUR_INTERRUPT_MSG,
  TOUR_SKIP_MSG,
  POST_TOUR_TIP,
  POST_TOUR_TIP_BUG,
  POST_TOUR_REPLAY,
  POST_TOUR_NUDGE,
  setOnboardingState,
} from "../onboarding/sayerTour";
import { POST_TOUR_EXAMPLES_KEY } from "../lib/forecast/storageKeys.js";
import { writeTourState, clearTourState } from "../lib/forecast/tourState.js";
import { entryToMessageBody } from "../lib/forecast/hydrate.js";
import { captureChatTarget, resolveChatTarget } from "../lib/forecast/chatTarget.js";
import { abortAllPending } from "../lib/util/abort.js";
import { callLlm } from "../lib/forecast/llmClient.js";
import { withBackground } from "../lib/forecast/contractBackground.js";
import { recentConversationText } from "../lib/forecast/conversationMemory.js";
import { webSearchClause } from "../lib/forecast/webSearchClause.js";
import { todayAnchor } from "../lib/util/todayAnchor.js";
import { getRunState, runStore } from "../store/runStore.js";
import { selectConversationEntries } from "../store/selectors.js";
// tour-fixture.json (~59 KB) is deliberately NOT imported statically — it's
// only needed while the tour runs, so startTour() dynamic-imports it and Vite
// splits it out of the main chunk.

// User-side acknowledgements (white, right-aligned) printed when the user
// performs a tour action — confirms the action and breaks up the narration.
const TOUR_ACK = {
  report: "Report opened",
  report_closed: "Report closed",
  panel_opened: "Drawer opened",
};

// The thread on screen at the moment a tour line is written: the tour narrates the screen,
// so each line captures the selection as it says it (lib/forecast/chatTarget.ts) — the
// sanctioned capture, resolved by id when the line is filed.
const liveTarget = () => captureChatTarget(getRunState());

/**
 * Onboarding-tour domain: resumeTour, signalTourAction, waitForTourAction,
 * endTour, startTour, skipTour, runPostTourTip, handleEndTourBtn,
 * answerTourAside, endPostTour, and the tour signal/restore effects.
 * RETIRED: first-contact tips (useOnboardingTips) replace the tour, which nothing starts any
 * more and whose leftover state resumeTour drops; the rest is due for deletion.
 * Moved verbatim from AppInner. No behavior change except the disclosed
 * tour-globals threading (__tourFixture/__authEnabled can't be seen from this
 * separate module — see getTourFixture/getAuthEnabled/setTourFixture in deps).
 * Bound to the run-store singleton (`getRunState` / `runStore.dispatch`): no store handles
 * are threaded in.
 */
export function useSayerTour({
  // callbacks (stable across render / from other hooks)
  submit,
  pushSayerMsg,
  // Debug web-search switch (see App.jsx). Only the mid-tour aside uses it — scripted tour
  // narration is fixed copy and never reaches an LLM.
  resetForecast,
  resetAllDrawerHelp,
  setDrawerHelpState,
  drawerSeenPendingRef,
  setInput,
  setChatOpen,
  autoZoom,
  openReport,
  closeReportRef,
  forecastAbortRef,
  pendingAbortsRef,
  setChatThinking,
  setLoading,
  setForecastingPhase,
  // KNOWN-SHARED refs/state (also referenced by non-tour code — declared in App.jsx or in useOnboardingWiring, the caller)
  contractNotePendingRef,
  lastTourPendingIdRef,
  postTourNudgeTimerRef,
  postTourTipRef,
  postTourTipRunRef,
  ninetyNotePendingRef,
  forecastNextRef,
  exampleRevealTimerRef,
  tourExampleRunIdRef,
  tourEndConfirmRef,
  closeHintShownRef,
  // render state the effects watch
  active,
  branches,
  hov,
  logOpen,
  contractOpen,
  showReport,
  tsDrawerOpen,
  hydrationDone,
  // tour-global accessors (threaded — see module doc)
  getAuthEnabled,
  setTourFixture,
}) {
  // Tour controls are reached through a ref (populated below, after the tour
  // controller is defined) so this callback doesn't depend on tour state that's
  // declared later — the forward-ref pattern `selectForecastRef`/`resumeTourRef` use.
  // (forecastNextRef is KNOWN-SHARED, declared in App.jsx; tourEndConfirmRef and
  // closeHintShownRef are declared in useOnboardingWiring, the caller; all three are passed in
  // above. tourExampleForecastRef below is tour-only.)
  // The tour's example forecast — captured at forecast_ready. If the user opens
  // the Forecast Log mid-tour and clicks a different forecast, we snap back to
  // this one when they close the drawer (the tour is about the example).
  const tourExampleForecastRef = useRef(null);

  // Shared runSayerTour invocation for both entry points (resumeTour and
  // startTour): identical say/waitFor/onStepChange/onDone wiring, differing
  // only in which id (`exampleId`) onStepChange persists into tourState, and
  // in the .catch log label (kept distinct — "resume" vs "runner" — since
  // it's dev-facing diagnostic text tied to which entry point failed). `ctx`
  // is NOT built in here: resumeTour and startTour each construct it from
  // their own locally re-imported `tourFixture` binding (the expressions are
  // textually identical today, but they're independent local values, not a
  // shared one) — see each call site. `token` stays a param too (each caller
  // owns its own tourTokenRef assignment). Defined as a plain function (not
  // useCallback) for the same reason as resumeTour below: it's only invoked
  // after render, once all the consts it closes over (pushSayerMsg/
  // waitForTourAction/endTour/setTourSpotlight/…) are initialized.
  const runTourSteps = ({ ctx, token, exampleId, startIndex = 0, resuming = false, catchLabel }) =>
    runSayerTour({
      say: (text, meta) => {
        setTourSayCount((n) => n + 1);
        return pushSayerMsg(text, { kind: "tour", legend: meta?.legend, target: liveTarget() });
      },
      waitFor: (key) => waitForTourAction(key),
      ctx,
      token,
      onStepChange: (step) => {
        setTourSpotlight(step?.spotlight || null);
        if (step?.examples) {
          forecastNextRef.current = true;
          exampleRevealTimerRef.current = setTimeout(() => setTourExamples(true), 2000);
          try {
            sessionStorage.setItem(POST_TOUR_EXAMPLES_KEY, "1");
          } catch {
            /* ignore */
          }
        }
        if (step)
          writeTourState({
            phase: "steps",
            exampleForecastId: exampleId,
            stepIndex: SAYER_STEPS.indexOf(step),
            awaitedAction: step.await || null,
            active: true,
          });
        else clearTourState();
      },
      onDone: () => endTour("completed"),
      startIndex,
      resuming,
    }).catch((e) => {
      console.error(catchLabel, e);
      endTour("interrupted");
    });

  // The guided tour is retired: first-contact tips (useOnboardingTips) introduce Sayer. A tour
  // left mid-way before that (its state in sessionStorage) is dropped, never resumed.
  const resumeTour = () => {
    clearTourState();
  };

  // ---- Onboarding tour (interaction-gated) --------------------------------
  // Creates a fixture-backed forecast, then walks the user through it one step
  // at a time — Sayer narrates, the user does the action (click the %, hover a
  // trigger, branch, open a panel), and only then does the next step appear.
  // See app/src/onboarding/sayerTour.js + docs/onboarding-tour-plan.md.
  const [tourActive, setTourActive] = useState(false);
  const [tourWaitKey, setTourWaitKey] = useState(null); // action the current step is waiting on (drives Next button / hints)
  const [tourExamples, setTourExamples] = useState(false); // show clickable example questions at the end (footer); on submit they move into history as a greyed message
  const [tourSpotlight, setTourSpotlight] = useState(null); // UI element the current step highlights (blinking lightbulb)
  const [enterFlash, setEnterFlash] = useState(false); // flashes the chat ENTER button during the tour's typed-question demo
  const [bugFlash, setBugFlash] = useState(false); // flashes the bug icon green during the post-tour bug/feature note
  const [endTourBtn, setEndTourBtn] = useState(false); // shows the "End Tour" button after the post-forecast bug/feature note
  // True across the post-tour coaching window — from when the interactive walk
  // completes until the user finishes (or dismisses) the green tips. Keeps a
  // "Skip Tour ✕" button in the chat footer so the escape hatch never vanishes
  // mid-coaching; it gives way to the "End Tour →" button (endTourBtn) once that
  // appears. Distinct from tourActive, which also gates the interactive-walk
  // machinery (spotlights, action signals) that must NOT run during coaching.
  const [postTourActive, setPostTourActive] = useState(false);
  // Bumped on every tour narration line, so the rail can flag one posted while Chat is hidden.
  const [tourSayCount, setTourSayCount] = useState(0);
  // True once this session starts the tour. Nothing starts it any more (first-contact tips
  // replace it), so suppressGuideNarration stays on — see below.
  const [tourStartedThisSession, setTourStartedThisSession] = useState(false);
  const tourTokenRef = useRef(null);
  const tourWaitRef = useRef(null); // {key, resolve} for the in-flight step's wait

  // Resolve the current step's wait — but only when the signalled action matches
  // the key that step is waiting on, so unrelated state changes are ignored.
  const signalTourAction = useCallback((key) => {
    const w = tourWaitRef.current;
    if (w && w.key === key) {
      tourWaitRef.current = null;
      setTourWaitKey(null);
      if (TOUR_ACK[key]) {
        // Dispatch tour ack to store (kind:'guide' — session-only, not persisted).
        const ackEntry = makeEntry({ role: "user", kind: "guide", content: TOUR_ACK[key] });
        // The thread the chat panel renders when the action lands; nothing when no forecast
        // is selected (the tour's example run is, while a step is waited on).
        const dest = resolveChatTarget(getRunState(), liveTarget());
        if (dest && !dest.draft) runStore.dispatch(entryAppended(dest.convId, ackEntry));
      }
      w.resolve();
    }
  }, []);
  const waitForTourAction = useCallback(
    (key) =>
      new Promise((resolve) => {
        tourWaitRef.current = { key, resolve };
        setTourWaitKey(key);
      }),
    [],
  );

  // End the tour (completed / skipped / interrupted). Cancels the token, unblocks
  // any pending wait so the runner exits, clears the fixture override, records state.
  const endTour = useCallback(
    (state) => {
      const token = tourTokenRef.current;
      if (token) {
        token.cancelled = true;
        tourTokenRef.current = null;
        if (tourWaitRef.current) {
          const w = tourWaitRef.current;
          tourWaitRef.current = null;
          w.resolve();
        }
      }
      tourExampleRunIdRef.current = null; // tour over → stop treating any run as the example
      setTourWaitKey(null);
      // Tear down any in-flight example forecast so the NEXT run (a replay, or the
      // user's own question) starts clean. Skipping mid-generation otherwise leaves
      // loading=true, and submit()'s `if(loading)return` guard would silently drop
      // the replay's forecast → empty timeline.
      if (forecastAbortRef.current) {
        forecastAbortRef.current.abort();
        forecastAbortRef.current = null;
      }
      // Also abort any in-flight branch / chat-classification calls (same teardown
      // the CLEAR + force-submit paths do). A tour branch still running here would
      // otherwise flip the global `loading` off later, underneath whatever the
      // user generates next.
      abortAllPending(pendingAbortsRef);
      setLoading(false);
      setForecastingPhase(null);
      setTourFixture(null);
      clearTourState(); // tour torn down (skip/interrupt/complete) → drop persisted resume state
      // Reaching the terminal "examples" step both persists POST_TOUR_EXAMPLES_KEY (so a
      // mid-coaching reload could re-surface the "submit your own question" invitation) and
      // fires onDone → endTour("completed"). Ending the tour is terminal: once it's over
      // (by any path) a reload should be a clean "completed" slate, not re-trigger that
      // reveal. Drop the key here — the in-session reveal is unaffected (it's driven by the
      // tourExamples React state / its 2s timer, not this key). Skip/interrupt never set it.
      try {
        sessionStorage.removeItem(POST_TOUR_EXAMPLES_KEY);
      } catch {
        /* private mode / quota */
      }
      setTourActive(false);
      setBugFlash(false);
      // A completed walk hands off to the post-tour coaching window (contract note
      // → "~a minute" → "ready" → bug note). Keep the footer's "Skip Tour ✕" escape
      // hatch alive through that whole stretch — it's replaced by "End Tour →" once
      // the user's first real forecast lands. Skip/interrupt end the tour outright.
      setPostTourActive(state === "completed");
      if (state === "skipped") {
        // Reply first, then (2s later) reveal the "Click an example…" prompt +
        // example questions, so they don't all paste in at once. No spotlight —
        // a lightbulb flashing by the chat on skip reads as a bug.
        setTourSpotlight(null);
        pushSayerMsg(TOUR_SKIP_MSG, { kind: "tour", target: liveTarget() });
        exampleRevealTimerRef.current = setTimeout(() => setTourExamples(true), 2000);
      } else if (state === "interrupted") {
        setTourSpotlight(null);
        pushSayerMsg(TOUR_INTERRUPT_MSG, { kind: "tour", target: liveTarget() });
      } else {
        setTourSpotlight(null);
      }
      setOnboardingState(state);
      // Arm the deferred add-your-own-trigger reminder for COMPLETERS only. Someone who
      // skipped or bailed out opted out of coaching; re-approaching them days later is
      // the version of this that reads as nagging rather than help.
      if (state === "completed") startCustomTriggerNudge();
      recordUiEvent({ event: "ui.tour.end", state });
      // Omitted deps are stable across renders (refs/setters/dispatch, or the
      // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
      // longer infer their stability. Verbatim from AppInner; adding them would churn.
    },
    [pushSayerMsg], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const startTour = useCallback(
    async ({ resumeFrom } = {}) => {
      if (tourTokenRef.current) return; // already running
      setTourStartedThisSession(true); // this session is running the tour → its narration stays visible
      recordUiEvent({ event: "ui.tour.start" });
      if (!resumeFrom) {
        // Fresh start (first-visit auto-start or Settings replay): clean slate. Any
        // prior forecast is already saved in the Forecast Log, so resetting loses nothing.
        setTourExamples(false);
        setEndTourBtn(false);
        setPostTourActive(false);
        closeHintShownRef.current = false;
        tourEndConfirmRef.current = null;
        tourExampleForecastRef.current = null;
        contractNotePendingRef.current = false;
        ninetyNotePendingRef.current = false;
        postTourTipRef.current = false;
        setChatOpen(true);
        resetForecast();
        resetAllDrawerHelp();
        drawerSeenPendingRef.current = {};
        setDrawerHelpState({
          contract: false,
          log: false,
          settings: false,
          timeseries: false,
          account: false,
          bug: false,
        });
      } else {
        // Resume into a PRE-FORECAST phase after a reload (resumeFrom = "greeting" |
        // "asking"). The greeting / question-format messages were restored from the
        // sessionStorage draft, so do NOT resetForecast (it would wipe them) and do NOT
        // re-emit them below — re-arm the state machine in place so the chat is unchanged.
        setChatOpen(true);
        tourExampleForecastRef.current = null;
      }
      const { default: tourFixture } = await import("../onboarding/tour-fixture.json");
      setTourFixture(tourFixture);
      setTourActive(true);
      const token = makeTourToken();
      tourTokenRef.current = token;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      // ── Greeting (awaits Continue) ──────────────────────────────────────────────
      // resumeFrom==="asking" skips the greeting+gate entirely (the user already passed
      // it pre-reload). Otherwise re-arm the gate; on a fresh start emit the greeting,
      // on resumeFrom==="greeting" it's already in the restored chat so don't re-emit.
      if (resumeFrom !== "asking") {
        writeTourState({
          phase: "greeting",
          exampleForecastId: null,
          stepIndex: null,
          awaitedAction: "continue",
          active: true,
        });
        if (!resumeFrom) pushSayerMsg(TOUR_INTRO, { kind: "tour", target: liveTarget() });
        await waitForTourAction("continue");
        if (token.cancelled) return;
      }

      // ── Asking (question-format line + typing theatre) ──────────────────────────
      writeTourState({
        phase: "asking",
        exampleForecastId: null,
        stepIndex: null,
        awaitedAction: null,
        active: true,
      });
      if (resumeFrom !== "asking") {
        // Fresh or resume-from-greeting: emit the question-format line and "type" the
        // example question, flashing ENTER. resume-from-asking skips this — both the
        // greeting and the question-format line are already restored from the draft.
        pushSayerMsg(TOUR_QUESTION_FORMAT, { kind: "tour", target: liveTarget() });
        await sleep(3500);
        for (let i = 1; i <= TOUR_QUESTION.length && !token.cancelled; i++) {
          setInput(TOUR_QUESTION.slice(0, i));
          await sleep(26);
        }
        if (token.cancelled) return;
        await sleep(1000); // pause 1s after typing finishes before "clicking" enter
        setEnterFlash(true);
        await sleep(550);
        setEnterFlash(false);
        if (token.cancelled) {
          setInput("");
          return;
        }
      }
      setInput("");

      // ── Submit, then defer the example forecast row creation ────────────────────
      // force=true: the scripted forecast must never be blocked by a stale `loading` flag.
      lastTourPendingIdRef.current = null; // clear so we read THIS submit's pendingId, not a prior tour's
      submit(TOUR_QUESTION, undefined, true);
      // Wait for the fixture forecast to land, then let its entry animation play out
      // on an IDLE main thread BEFORE the row-creation/persistence work below. The
      // reveal cascade is a setInterval (~160ms/event); running the fetch/persist/poll
      // here (as this used to) starved that timer, so events popped in in clumps
      // instead of a smooth one-by-one cascade. Deferring the side-work makes the
      // tour's first forecast animate exactly like a real user forecast (whose reveal
      // always runs on a settled thread ~1min after submit). Tradeoff: a reload during
      // this ~2.5s window resumes at "asking" (re-runs) rather than "building" — rare,
      // and graceful, since building-phase resume needs the deferred DB row anyway.
      await waitForTourAction("forecast_ready");
      if (token.cancelled) return;
      await sleep(2500); // reveal window (~2.4s) — thread stays idle so the cascade is smooth
      if (token.cancelled) return;
      // Create the example forecast's SERVER ROW (deferred until after the reveal above) —
      // so the building-phase chat persists and a reload restores it + resumes at the
      // exact step (instead of re-running). The fixture forecast is instant and never
      // hits the streaming onTraceAllocated path, so run.forecastId would otherwise stay
      // null and persistMessage(null,…) be a no-op. POST a wire row built from the baked
      // fixture (the post-generation save-effect upserts the SAME client_id row with the
      // completed shape), stamp the returned UUID, and back-fill the greeting /
      // question-format entries adopted into the run at submit. exampleRunId comes from
      // the ref submit() just stamped (the selection is not read: this is the tour's run).
      // Gate on the LIVE __authEnabled mirror — the auto-start tour can race the mount
      // /api/me fetch, so the closure-captured authEnabled may be a stale false. Graceful:
      // on any failure the tour still runs; only this session's chat won't persist.
      // submit() has an early `await guardForTos()`, so it stamps lastTourPendingIdRef a
      // few ms AFTER returning — poll briefly for it (clearing above means we never read
      // a prior tour's id).
      let exampleRunId = null;
      for (let i = 0; i < 150 && !exampleRunId && !token.cancelled; i++) {
        exampleRunId = lastTourPendingIdRef.current;
        if (!exampleRunId) await sleep(20);
      }
      if (token.cancelled) return;
      tourExampleRunIdRef.current = exampleRunId; // mark for pushSayerMsg's persistence gate (survives reload via resumeTour)
      if (exampleRunId && getAuthEnabled()) {
        try {
          const base = tourFixture.base;
          const now = new Date();
          const events = (base.events || []).map((e) => {
            const d = new Date(e.date);
            const dEnd = e.date_end ? new Date(e.date_end) : d;
            return { ...e, isPast: (isNaN(dEnd.getTime()) ? d : dEnd) <= now };
          });
          const wire = {
            id: exampleRunId,
            question: TOUR_QUESTION,
            // The fixture is a recorded result wire, which says `contract.context`.
            contract: withBackground(base.contract),
            events,
            serverPrediction:
              typeof base.contract?.probability === "number" ? base.contract.probability : 0,
            serverExplanation: base.explanation || "",
            report: base.report || null,
            serverContext: {
              explanation: base.explanation || "",
              metadata: {},
              question: {},
              report: base.report || null,
            },
            _series: {},
          };
          const r = await postForecastRow(wire);
          if (r.ok) {
            const { forecastId } = await r.json();
            if (forecastId && !token.cancelled) {
              runStore.dispatch(traceAllocated(exampleRunId, { forecastId }));
              // The LIVE store holds the run + its adopted draft entries (greeting +
              // question-format), read by the example's id captured above. Back-fill persists
              // them once; later step narration persists live via pushSayerMsg's
              // isExampleForecast gate (no double-write).
              const preStampEntries = selectConversationEntries(getRunState(), exampleRunId);
              for (const e of preStampEntries) persistMessage(forecastId, entryToMessageBody(e));
              writeTourState({
                phase: "building",
                exampleForecastId: exampleRunId,
                stepIndex: null,
                awaitedAction: null,
                active: true,
              });
            }
          }
        } catch (_e) {
          /* graceful: tour still runs, chat just won't persist this session */
        }
      }
      const ctx = {
        pct: Math.round((tourFixture.base?.contract?.probability || 0) * 100),
        openReport: () => openReport(true),
      };
      // Deliberately fire-and-forget (startTour resolves while the runner walks
      // the steps) — so an unexpected throw inside say/waitFor/onStepChange must
      // be caught (inside runTourSteps) or it becomes an unhandled rejection and
      // teardown never runs.
      runTourSteps({ ctx, token, exampleId: exampleRunId, catchLabel: "tour runner failed:" });
      // setDrawerHelpState + drawerSeenPendingRef are stable (setState + ref) — safe to omit.
    },
    [resetForecast, pushSayerMsg, submit, waitForTourAction, endTour], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const skipTour = useCallback(() => {
    emitTourEnd("skipped");
    endTour("skipped");
  }, [endTour]);

  // Timed post-tour tip, fired from doForecast once the user's first forecast
  // lands: 1.5s → "Your forecast is ready."; 3s later → the bug/feature note (with
  // a lightbulb flash on the bug icon) + an "End Tour" button. Clicking End Tour
  // posts the replay hint. Triggered from doForecast via postTourTipRunRef.
  const runPostTourTip = useCallback(async () => {
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    await sleep(1500);
    pushSayerMsg(POST_TOUR_TIP, { kind: "tour", target: liveTarget() });
    await sleep(3000);
    pushSayerMsg(POST_TOUR_TIP_BUG, { kind: "tour", target: liveTarget() });
    setBugFlash(true);
    setTimeout(() => setBugFlash(false), 8000);
    setEndTourBtn(true);
  }, [pushSayerMsg]);
  postTourTipRunRef.current = runPostTourTip;
  // "End Tour" button (shown after the bug/feature note) → posts the replay hint.
  const handleEndTourBtn = useCallback(() => {
    emitTourEnd("completed");
    setEndTourBtn(false);
    setPostTourActive(false);
    pushSayerMsg(POST_TOUR_REPLAY, { kind: "tour", target: liveTarget() });
    // If the user doesn't submit anything for 45s, nudge them to try their own
    // question. submit() clears this timer, so a forecast after End Tour skips it.
    if (postTourNudgeTimerRef.current) clearTimeout(postTourNudgeTimerRef.current);
    postTourNudgeTimerRef.current = setTimeout(() => {
      postTourNudgeTimerRef.current = null;
      pushSayerMsg(POST_TOUR_NUDGE, { kind: "tour", target: liveTarget() });
    }, 45000);
    // Omitted deps are stable across renders (refs/setters/dispatch, or the
    // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
    // longer infer their stability. Verbatim from AppInner; adding them would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushSayerMsg]);
  // Footer "Skip Tour ✕" during the post-tour coaching window (before "End Tour →"
  // appears). Stops the remaining green tips and clears the example chips without
  // touching any forecast the user has already kicked off, then signs off with the
  // replay hint — same conclusion as clicking "End Tour →".
  const endPostTour = useCallback(() => {
    emitTourEnd("skipped");
    forecastNextRef.current = false;
    postTourTipRef.current = false;
    ninetyNotePendingRef.current = false;
    contractNotePendingRef.current = false;
    if (exampleRevealTimerRef.current) {
      clearTimeout(exampleRevealTimerRef.current);
      exampleRevealTimerRef.current = null;
    }
    if (postTourNudgeTimerRef.current) {
      clearTimeout(postTourNudgeTimerRef.current);
      postTourNudgeTimerRef.current = null;
    }
    setTourExamples(false);
    setEndTourBtn(false);
    setBugFlash(false);
    setPostTourActive(false);
    pushSayerMsg(POST_TOUR_REPLAY, { kind: "tour", target: liveTarget() });
    recordUiEvent({ event: "ui.tour.end", state: "post_tour_skipped" });
    // Omitted deps are stable across renders (refs/setters/dispatch, or the
    // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
    // longer infer their stability. Verbatim from AppInner; adding them would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushSayerMsg]);

  // Mid-tour aside: the user asked Sayer a question that isn't a forecast request.
  // Answer it briefly via the LLM (styled as tour narration) without interrupting
  // the tour — the current step's wait stays armed, so the tour resumes after.
  // Always on: a mid-tour aside is an ordinary chat answer, so it gets the same tool the
  // same question would a minute later.
  const webSearchOn = true;
  const answerTourAside = useCallback(
    async (q) => {
      setChatThinking(true);
      // Track the fetch in pendingAbortsRef so CLEAR / endTour teardown cancels it —
      // otherwise a reset mid-flight would still resolve and push a stale tour aside.
      const ac = new AbortController();
      pendingAbortsRef.current.add(ac);
      // The thread the question was asked in: captured before the model call, so an answer that
      // lands after the user switched runs still files under the asking thread.
      const target = liveTarget();
      try {
        // Recent transcript (so a tour follow-up keeps context, #3525) + a current-date
        // anchor (#3523).
        const memory = recentConversationText(getRunState());
        const prompt = `You are Sayer, an AI forecasting assistant, guiding a new user through a short onboarding tour. The user paused to ask a question. Answer it helpfully and concisely (1-3 sentences), in a friendly tone; if it's about how Sayer works, explain plainly. Do not start a forecast.\n\n${todayAnchor()}\n\nRECENT CONVERSATION:\n${memory || "(none)"}\n\n${webSearchClause(webSearchOn)}\n\nUSER QUESTION: "${q}"\n\nReply with ONLY your answer text (no JSON, no markdown).`;
        const raw = await callLlm(prompt, { signal: ac.signal, webSearch: webSearchOn });
        if (ac.signal.aborted) {
          setChatThinking(false);
          return;
        }
        const text = raw.trim();
        setChatThinking(false);
        pushSayerMsg(text || "Good question — let's keep going with the tour.", {
          kind: "tour",
          target,
        });
      } catch (e) {
        if (e.name === "AbortError") {
          setChatThinking(false);
          return;
        }
        setChatThinking(false);
        pushSayerMsg("Let's keep going with the tour.", { kind: "tour", target });
      } finally {
        pendingAbortsRef.current.delete(ac);
      }
      // Omitted deps are stable across renders (refs/setters/dispatch, or the
      // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
      // longer infer their stability. Verbatim from AppInner; adding them would churn.
    },
    // `webSearchOn` IS listed: it is read from the render closure inside, and unlike the
    // refs/setters above it genuinely changes when the user flips the checkbox.
    [pushSayerMsg, webSearchOn], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Tour first-forecast: once the preprocess/contract panel is DISMISSED (the user
  // clicked Continue, or it auto-timed-out), wait ~2s then post "A forecast may
  // take a minute to generate." Watches pendingPreprocess for a non-null→null edge.
  // (prevPreprocessOpenRef + the effect that reads it stay in App — see the
  // preprocess-note effect, which is NOT tour code.)

  // Action signals — each fires when its action happens during the tour;
  // signalTourAction no-ops unless the current step is waiting on that key.
  // tourWaitKey is in the deps so a wait that's armed AFTER its condition is
  // already true (e.g. the report was closed during the prior step's pause)
  // still resolves immediately instead of deadlocking.
  useEffect(() => {
    if (
      tourActive &&
      active &&
      !active._pending &&
      active.contract &&
      active.contract.probability != null
    ) {
      if (!tourExampleForecastRef.current) tourExampleForecastRef.current = active;
      signalTourAction("forecast_ready");
    }
  }, [tourActive, active, tourWaitKey, signalTourAction]);
  // Snap back to the example forecast when the Forecast Log drawer closes during
  // the tour (the user may have clicked a different row to peek at it).
  useEffect(() => {
    if (!tourActive || logOpen) return;
    const ex = tourExampleForecastRef.current;
    if (ex && active && active.id !== ex.id) {
      runStore.dispatch(activeSet(ex.id)); // dual-write tour snap-back active to store
      if (ex.contract?.resolution_date) autoZoom(ex.contract.resolution_date);
    }
  }, [tourActive, logOpen, active, autoZoom]);
  useEffect(() => {
    if (tourActive) signalTourAction(showReport ? "report" : "report_closed");
  }, [tourActive, showReport, tourWaitKey, signalTourAction]);
  // During the tour, ANY click outside the report panel closes it — including the
  // chat panel, which is z-raised above the report's own backdrop so its clicks
  // wouldn't reach the backdrop's close handler.
  useEffect(() => {
    if (!tourActive || !showReport) return;
    const onDown = (e) => {
      const t = e.target;
      if (!(t && t.closest && t.closest("[data-report]"))) closeReportRef.current();
    };
    // telemetry-gesture: an outside click closes the report during the tour (view state)
    document.addEventListener("mousedown", onDown, true);
    return () => document.removeEventListener("mousedown", onDown, true);
    // Omitted deps are stable across renders (refs/setters/dispatch, or the
    // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
    // longer infer their stability. Verbatim from AppInner; adding them would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourActive, showReport]);
  useEffect(() => {
    if (tourActive) signalTourAction(contractOpen ? "contract" : "contract_closed");
  }, [tourActive, contractOpen, tourWaitKey, signalTourAction]);
  // Branch count from the store-derived `branches` (active's run tree).
  useEffect(() => {
    if (tourActive && branches.length > 0) signalTourAction("branch");
  }, [tourActive, branches.length, tourWaitKey, signalTourAction]);
  // Hover step: advance only after the user SUSTAINS a hover over a trigger for
  // 2s (so the tooltip has actually been up that long) — OR, if they never do,
  // after a 35s fallback that moves on to the "click it" step anyway.
  useEffect(() => {
    if (!tourActive || tourWaitKey !== "hover") return;
    const fallback = setTimeout(() => signalTourAction("hover"), 35000);
    return () => clearTimeout(fallback);
  }, [tourActive, tourWaitKey, signalTourAction]);
  useEffect(() => {
    if (!tourActive || tourWaitKey !== "hover") return;
    if (hov && !String(hov).startsWith("res") && !String(hov).startsWith("rbr")) {
      const t = setTimeout(() => signalTourAction("hover"), 2000);
      return () => clearTimeout(t);
    }
  }, [tourActive, tourWaitKey, hov, signalTourAction]);
  // The final step asks the user to open one of the spotlighted edge panels
  // (Forecast Log / Contract / Signal Library — not the Sayer chat).
  useEffect(() => {
    if (tourActive && (logOpen || contractOpen || tsDrawerOpen)) {
      signalTourAction("panel_opened");
      setTourSpotlight((s) => (s === "tab" ? null : s));
      setBugFlash(false);
    }
  }, [tourActive, logOpen, contractOpen, tsDrawerOpen, tourWaitKey, signalTourAction]);
  // After the user has explored a panel, "Those are the basics" waits until all
  // panels are closed AND none is reopened for 2s (the timer resets if one opens).
  useEffect(() => {
    if (!tourActive || tourWaitKey !== "panels_settled") return;
    if (logOpen || contractOpen || tsDrawerOpen) {
      // A drawer is open — if it stays open 10s (without opening another, which
      // would re-run this effect and reset the timer), nudge how to close it.
      const t = setTimeout(() => {
        if (!closeHintShownRef.current) {
          closeHintShownRef.current = true;
          pushSayerMsg("**Click Chat** on the left to come back.", {
            kind: "tour",
            target: liveTarget(),
          });
        }
      }, 10000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      // The rail's panel may be collapsed; the tour speaks in the chat.
      setChatOpen(true);
      signalTourAction("panels_settled");
    }, 2000);
    return () => clearTimeout(t);
    // Omitted deps are stable across renders (refs/setters/dispatch, or the
    // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
    // longer infer their stability. Verbatim from AppInner; adding them would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tourActive,
    tourWaitKey,
    logOpen,
    contractOpen,
    tsDrawerOpen,
    signalTourAction,
    pushSayerMsg,
  ]);

  // Re-show the post-tour "submit your own question" example-questions invitation after
  // a reload. The tour ends at its terminal examples step (tour state cleared), and the
  // examples reveal is transient UI — so a reload would otherwise drop it. The flag is
  // set when that step is reached and cleared once the user submits their own forecast or
  // clears. Run once after hydration; skip if a tour is actively resuming.
  const postTourExamplesRestoredRef = useRef(false);
  useEffect(() => {
    if (postTourExamplesRestoredRef.current || !hydrationDone) return;
    postTourExamplesRestoredRef.current = true;
    try {
      if (sessionStorage.getItem(POST_TOUR_EXAMPLES_KEY) && !tourActive) {
        setChatOpen(true);
        setTourExamples(true);
        setPostTourActive(true);
      }
    } catch {
      /* ignore */
    }
    // Omitted deps are stable across renders (refs/setters/dispatch, or the
    // getAuthEnabled/setTourFixture thunks) passed as hook args — eslint can no
    // longer infer their stability. Verbatim from AppInner; adding them would churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationDone, tourActive]);

  // Hide the tour's scripted narration (entryKind "guide") unless this session ran the tour.
  // The render layer filters guide items out of the chat, so lines left in an example
  // forecast's saved thread or a mid-tour draft (a Continue prompt with no Continue control)
  // never show. The user's own conversation is untouched.
  const suppressGuideNarration = !tourStartedThisSession;

  return {
    tourActive,
    tourWaitKey,
    tourExamples,
    tourSpotlight,
    tourSayCount,
    suppressGuideNarration,
    enterFlash,
    bugFlash,
    endTourBtn,
    postTourActive,
    setTourExamples,
    resumeTour,
    startTour,
    skipTour,
    endTour,
    signalTourAction,
    answerTourAside,
    handleEndTourBtn,
    endPostTour,
  };
}
